import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Send, MessageCircle, Mail, Phone } from 'lucide-react';
import { BrandLogo } from '@/components/layout/BrandLogo.js';
import { bankLogoUrl } from '@/components/account/BankLogo.js';
import { useBrandingStore } from '@/stores/branding.js';
import type { ReactNode } from 'react';

const PAYMENT_CHIPS: { label: string; color: string; code?: string }[] = [
  { label: 'SCB',          color: '#4d2c8b', code: 'SCB' },
  { label: 'KBank',        color: '#0a8e3a', code: 'KBANK' },
  { label: 'Bangkok Bank', color: '#00529d', code: 'BBL' },
  { label: 'PromptPay',    color: '#003e89' },
];

const SOCIALS = [
  { Icon: MessageCircle, label: 'LINE' },
  { Icon: Send,          label: 'Telegram' },
  { Icon: Mail,          label: 'Email' },
  { Icon: Phone,         label: 'Call' },
];

const ITEM =
  'w-max text-[13px] leading-none text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]';

function Col({ title, children }: { title: string; children: ReactNode }) {
  return (
    <nav className="flex flex-col gap-4">
      <h3 className="text-[12.5px] font-bold text-[var(--text-primary)]">{title}</h3>
      <div className="flex flex-col gap-3">{children}</div>
    </nav>
  );
}

export function SiteFooter() {
  const { t } = useTranslation('common');
  const sitename = useBrandingStore((s) => s.branding?.sitename) ?? '';
  const year = new Date().getFullYear();
  return (
    <footer className="mt-20 flex flex-col gap-12 pb-4 text-[var(--text-secondary)]">
      {/* Brand + link columns */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-12 md:grid-cols-[1.8fr_1fr_1fr_1fr]">
        <div className="col-span-2 flex flex-col gap-6 md:col-span-1 md:pr-8">
          <BrandLogo size={30} />
          <p className="max-w-xs text-[13px] leading-relaxed text-[var(--text-secondary)]">
            {t('footer.tagline')}
          </p>

          <div className="flex flex-col gap-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">
              {t('footer.community', { defaultValue: 'ติดตามเรา' })}
            </span>
            <div className="flex gap-2.5">
              {SOCIALS.map(({ Icon, label }) => (
                <span
                  key={label}
                  aria-label={label}
                  className="grid h-9 w-9 cursor-pointer place-items-center rounded-lg bg-white/[0.05] text-[var(--text-secondary)] transition-colors hover:bg-[var(--accent)] hover:text-[var(--accent-on)]"
                >
                  <Icon className="size-[18px]" aria-hidden />
                </span>
              ))}
            </div>
          </div>
        </div>

        <Col title={t('footer.games')}>
          <Link to="/casino" className={ITEM}>{t('nav.casino')}</Link>
          <Link to="/lottery" className={ITEM}>{t('nav.lotto')}</Link>
          <Link to="/promos" className={ITEM}>{t('nav.promo')}</Link>
        </Col>

        <Col title={t('footer.account')}>
          <Link to="/wallet" className={ITEM}>{t('nav.wallet')}</Link>
          <Link to="/history" className={ITEM}>{t('nav.history')}</Link>
          <Link to="/profile" className={ITEM}>{t('nav.profile')}</Link>
          <Link to="/promos" search={{ tab: 'referral' }} className={ITEM}>{t('nav.referral')}</Link>
        </Col>

        <Col title={t('footer.support')}>
          <span className={`${ITEM} cursor-default`}>{t('footer.terms')}</span>
          <span className={`${ITEM} cursor-default`}>{t('footer.privacy')}</span>
          <span className={`${ITEM} cursor-default`}>{t('footer.responsiblePlay')}</span>
        </Col>
      </div>

      {/* Accepted payment methods */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">
          {t('footer.payment.title')}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {PAYMENT_CHIPS.map((p) => {
            const logo = p.code ? bankLogoUrl(p.code) : null;
            return logo ? (
              <img key={p.label} src={logo} alt={p.label} className="h-7 w-auto rounded-md border border-[var(--border)] object-contain" loading="lazy" />
            ) : (
              <span
                key={p.label}
                className="rounded-md px-2.5 py-1 text-[11px] font-bold text-white"
                style={{ background: p.color }}
              >
                {p.label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Fine print + bottom bar */}
      <div className="flex flex-col gap-5">
        <p className="max-w-3xl text-[12px] leading-relaxed text-[var(--text-muted)]">
          {t('footer.legal', { sitename })}
        </p>
        <div className="flex flex-col gap-3 text-[12px] text-[var(--text-muted)] sm:flex-row sm:items-center sm:justify-between">
          <span>{t('footer.copyright', { year, sitename })}</span>
          <span className="inline-flex items-center gap-2">
            <span className="grid h-6 min-w-6 place-items-center rounded-md bg-white/[0.06] px-1.5 text-[10px] font-extrabold text-[var(--accent-strong)]">
              18+
            </span>
            {t('footer.responsiblePlay')}
          </span>
        </div>
      </div>
    </footer>
  );
}
